import { aiAllowed } from "@/lib/ai-access";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Brain, CodeXml, History, KeyRound, LogOut, Sparkles, WandSparkles } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { create } from "zustand";
import { toast } from "sonner";
import { openChat } from "@/desktop/chat";
import { Markdown } from "@/components/markdown";
import { Button } from "@/components/ui/button";
import { MenuItem, MenuLabel, MenuSeparator } from "@/components/ui/overlay";
import { ResponseActions, Thought } from "@/components/chat-parts";
import {
  ChatSessionBar,
  findSession,
  revealMessage,
  sessionTitle,
  useChatHistory,
  type ChatSession,
} from "@/components/chat-sessions";
import { Spinner } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import {
  AI_EFFORTS,
  CLAUDE_MODELS,
  claudeCost,
  claudeModelName,
  isAiEffort,
  isClaudeModel,
  type AiEffort,
  type ClaudeModelId,
} from "@ide/shared";
import { streamChat } from "@/lib/ai";
import { useAiUsage } from "@/lib/ai-usage";
import { AI_BASE, api, errorMessage } from "@/lib/api";
import { useAgent } from "@/plugins/ai-plugins";
import { ClaudeLogin, runClaudeInTerminal } from "@/plugins/claude-login";
import { sendToAgent, useAgentStore } from "@/assistant/agent-store";
import { useDesktopUi } from "@/desktop/ui";
import { ProviderMark } from "./chat-header";
import {
  attachedFilesText,
  ChatInput,
  ChatPicker,
  ChatWelcome,
  chatContext,
  currentEditorContext,
  INTELLIGENCE_LABEL,
  ModelPicker,
  openPath,
  RequestBubble,
  ResponseHeader,
  StatusChip,
  SuggestedActions,
} from "@/components/ai-chat-ui";
import { FileTypeIcon } from "@/components/file-icons";
import { cn, timeAgo } from "@/lib/utils";
import { useWs, type WorkspaceContextValue } from "./context";
import { useWorkspace } from "./store";

/**
 * Perguntas vindas de fora do painel (comandos do plugin Copiloto): o texto chega no campo de
 * mensagem, com a seleção marcada, para a pessoa revisar e enviar.
 */
export const useAiPrompt = create<{ pending: string | null; ask(text: string): void; take(): string | null }>(
  (set, get) => ({
    pending: null,
    ask: (text) => {
      set({ pending: text });
      // O chat com o Claude fica na lateral direita (como no VS Code); sem o plugin, avisa.
      openChat("claude");
    },
    take: () => {
      const text = get().pending;
      if (text !== null) set({ pending: null });
      return text;
    },
  }),
);

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  /** Anexos que foram junto com o pedido. */
  context?: string[];
  thinking?: string;
  files?: string[];
  error?: string;
  /** Quem respondeu ("Sonnet 5.5", "Auto · Haiku 5.5"). */
  model?: string;
  rating?: "up" | "down";
  startedAt?: number;
  doneAt?: number;
  /** Quanto tempo pensou antes de começar a responder. */
  thinkingMs?: number;
}

/** Modelo escolhido no chat: um do Claude ou "Auto" (o Kompil escolhe a cada pedido). */
type ModelChoice = ClaudeModelId | "auto";

/**
 * Modelo automático: Haiku para perguntas curtas, Opus para pedidos grandes ou difíceis (ou com
 * esforço alto) e Sonnet para o resto — como o "Auto" do VS Code.
 */
export function autoClaudeModel(text: string, effort: AiEffort): ClaudeModelId {
  const hard = /arquitet|refator|projet|depur|investig|migr|otimiz|segurança|concorr|complex/i.test(text);
  if (effort === "high" || hard || text.length > 1_200) return "claude-opus-5-5";
  if (text.length < 140 && !/```|\bbug\b|erro|corrig|implement|teste|cri[ae]/i.test(text))
    return "claude-haiku-5-5";
  return "claude-sonnet-5-5";
}

/** Resumo "o que mudou desde a sua última visita" (seção 8). */
function SinceLastVisit({ since, changedFiles }: { since: string; changedFiles: number }) {
  const { detail } = useWs();
  const summary = useMutation({
    mutationFn: () =>
      api<{ summary: string; changedFiles: string[] }>("/summary", {
        method: "POST",
        base: AI_BASE,
        body: { projectId: detail.project.id, since },
      }),
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="hud-corners m-3 border border-border p-3">
      <div className="flex items-center gap-2 text-[12.5px]">
        <History className="h-3.5 w-3.5" />
        <span>
          <strong>{changedFiles}</strong> arquivo{changedFiles > 1 ? "s mudaram" : " mudou"} desde a sua
          última visita ({timeAgo(since)}).
        </span>
      </div>
      {summary.data ? (
        <div className="mt-2.5 border-t border-border pt-2.5">
          <Markdown text={summary.data.summary} />
        </div>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          className="mt-2.5"
          loading={summary.isPending}
          onClick={() => summary.mutate()}
        >
          <Sparkles className="h-3.5 w-3.5" /> Resumir com IA
        </Button>
      )}
    </div>
  );
}

/** Visualizadores não usam a IA no projeto: só leem o código e conversam com o time. */
export function AiBlockedGate() {
  return (
    <div className="p-4 text-[13px]">
      <Brain className="mb-3 h-5 w-5 text-fg-subtle" />
      <p className="font-medium">IA indisponível para visualizadores</p>
      <p className="mt-1.5 text-fg-muted">
        Quem só visualiza o projeto lê o código e conversa com o time no chat. Para usar o Claude ou o
        Copilot, peça ao dono um papel de editor.
      </p>
    </div>
  );
}

const EMPTY: ChatMessage[] = [];

/** Conversa com o Claude por projeto: sobrevive a fechar e abrir a lateral. */
const useAiChat = create<{
  projectId: string | null;
  messages: ChatMessage[];
  /** Conversa no histórico (null: ainda sem mensagem). */
  historyId: string | null;
  setMessages(projectId: string, next: ChatMessage[] | ((all: ChatMessage[]) => ChatMessage[])): void;
}>((set) => ({
  projectId: null,
  messages: [],
  historyId: null,
  setMessages: (projectId, next) =>
    set((s) => {
      const same = s.projectId === projectId;
      const current = same ? s.messages : [];
      return {
        projectId,
        historyId: same ? s.historyId : null,
        messages: typeof next === "function" ? next(current) : next,
      };
    }),
}));

/** Guarda a conversa do Claude no histórico (título, modelo e mensagens). */
function saveClaudeHistory(projectId: string) {
  const st = useAiChat.getState();
  if (st.projectId !== projectId || !st.messages.length) return;
  const id = st.historyId ?? crypto.randomUUID();
  const prev = findSession("claude", id);
  useChatHistory.getState().save({
    id,
    provider: "claude",
    projectId,
    title: prev?.title ?? sessionTitle(st.messages.find((m) => m.role === "user")?.content ?? ""),
    createdAt: prev?.createdAt ?? Date.now(),
    updatedAt: Date.now(),
    model: [...st.messages].reverse().find((m) => m.model)?.model ?? null,
    bookmarked: prev?.bookmarked ?? false,
    bookmarks: prev?.bookmarks ?? [],
    messages: st.messages,
  });
  if (st.historyId !== id) useAiChat.setState({ historyId: id });
}

/** Agora (ms), fora do componente: usado nos eventos de envio. */
const nowMs = () => Date.now();

/** "Criar o CLAUDE.md" (como o /init do Claude Code): o que a IA precisa saber sobre o projeto. */
const CLAUDE_MD_PROMPT =
  "Analise este projeto e escreva o conteúdo de um CLAUDE.md: o objetivo do projeto, as tecnologias, como rodar e testar, as convenções de código e as pastas importantes. Responda só com o Markdown do arquivo.";

const SUGGESTIONS = [
  "Explique o que este arquivo faz",
  "Onde está o tratamento de erros?",
  "Escreva um teste para a função selecionada",
  "Sugira melhorias de desempenho",
];

/**
 * Modo do chat do Claude: "Perguntar" responde aqui (lê o projeto, não altera nada); "Agente" passa o
 * pedido ao Agente da lateral, que edita arquivos e roda comandos pedindo permissão.
 */
export type ClaudeMode = "ask" | "agent";

/** Modelo, esforço e modo escolhidos (lembrados neste computador). */
const useClaudePrefs = create<{
  model: ModelChoice;
  effort: AiEffort;
  mode: ClaudeMode;
  set(p: Partial<{ model: ModelChoice; effort: AiEffort; mode: ClaudeMode }>): void;
}>((set) => {
  let model: ModelChoice = "auto";
  let effort: AiEffort = "medium";
  let mode: ClaudeMode = "ask";
  try {
    const m = localStorage.getItem("kompil.claude.model");
    const e = localStorage.getItem("kompil.claude.effort");
    if (isClaudeModel(m) || m === "auto") model = m;
    if (isAiEffort(e)) effort = e;
    if (localStorage.getItem("kompil.claude.mode") === "agent") mode = "agent";
  } catch {
    // sem armazenamento: o padrão
  }
  return {
    model,
    effort,
    mode,
    set: (p) => {
      try {
        if (p.model) localStorage.setItem("kompil.claude.model", p.model);
        if (p.effort) localStorage.setItem("kompil.claude.effort", p.effort);
        if (p.mode) localStorage.setItem("kompil.claude.mode", p.mode);
      } catch {
        // sem armazenamento: vale só agora
      }
      set(p);
    },
  };
});

const CONSOLE_USAGE = "https://console.anthropic.com/settings/usage";

/** Pedido no modo Agente: vai para o Agente da lateral (com a chave de IA conectada nele). */
function sendToSideAgent(ws: WorkspaceContextValue, text: string) {
  useDesktopUi.getState().set({ side: "agent" });
  if (!useAgentStore.getState().provider) {
    toast("Conecte uma chave de IA no Agente", {
      description: "Cole a chave (Anthropic, OpenAI, Gemini…) e o Agente identifica o provedor.",
    });
    return;
  }
  void sendToAgent({ ws, text, withSelection: true });
}

/** Conta da Anthropic conectada no app (a chave fica no cofre do sistema). */
export function useClaudeAccount() {
  return useQuery({
    queryKey: ["claude", "account"],
    queryFn: () => desktop!.claude.status(),
    enabled: Boolean(desktop),
    staleTime: 5 * 60_000,
  });
}

export function AiPanel({
  visit,
}: {
  visit: { previousVisitAt: string | null; changedFiles: number } | null;
}) {
  const wsValue = useWs();
  const { detail, config } = wsValue;
  const active = useWorkspace((s) => s.active);
  const autocomplete = useWorkspace((s) => s.autocomplete);
  const setAutocomplete = useWorkspace((s) => s.setAutocomplete);
  const projectId = detail.project.id;
  const messages = useAiChat((s) => (s.projectId === projectId ? s.messages : EMPTY));
  const historyId = useAiChat((s) => (s.projectId === projectId ? s.historyId : null));
  const setMessages = (next: ChatMessage[] | ((all: ChatMessage[]) => ChatMessage[])) =>
    useAiChat.getState().setMessages(projectId, next);
  const { model, effort, mode, set: setPrefs } = useClaudePrefs();
  const canAgent = Boolean(desktop?.assistant);
  const agentMode = canAgent && mode === "agent";
  const account = useClaudeAccount();
  const qc = useQueryClient();
  const own = Boolean(account.data?.connected);
  const [connecting, setConnecting] = useState(false);
  const [input, setInput] = useState(() => useAiPrompt.getState().take() ?? "");
  const [streaming, setStreaming] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Pedido do Copiloto com o painel já aberto: preenche o campo e foca.
  useEffect(
    () =>
      useAiPrompt.subscribe((s) => {
        if (s.pending === null) return;
        setInput(useAiPrompt.getState().take() ?? "");
        chatContext.claude.getState().set({ includeSelection: true });
        inputRef.current?.focus();
      }),
    [],
  );
  const abort = useRef<AbortController | null>(null);
  const ownRequest = useRef<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [messages]);
  useEffect(
    () => () => {
      abort.current?.abort();
      if (ownRequest.current) desktop?.claude.cancel(ownRequest.current);
    },
    [],
  );
  useEffect(() => inputRef.current?.focus(), []);

  if (account.isLoading && desktop) {
    return (
      <div className="flex justify-center pt-10">
        <Spinner />
      </div>
    );
  }
  // Sem a conta da pessoa e sem a IA do servidor: como entrar (igual ao Claude Code no VS Code).
  if ((!own && !config.aiAvailable) || connecting) {
    return <ClaudeLogin onDone={() => setConnecting(false)} />;
  }
  if (!aiAllowed(detail)) return <AiBlockedGate />;

  const stop = () => {
    abort.current?.abort();
    if (ownRequest.current) desktop?.claude.cancel(ownRequest.current);
  };

  const pushError = (message: string) =>
    setMessages((all) => [...all.slice(0, -1), { ...all[all.length - 1]!, error: message }]);

  const send = async (retry?: string) => {
    const text = (retry ?? input).trim();
    if (!text || streaming) return;
    const { activePath, selection: sel } = currentEditorContext("claude");
    const selection = sel?.text;
    const attached = await attachedFilesText("claude");
    const labels = [
      ...(activePath
        ? [
            `${activePath.split("/").pop()}${sel ? `:${sel.from}${sel.to !== sel.from ? `-${sel.to}` : ""}` : ""}`,
          ]
        : []),
      ...chatContext.claude.getState().files.map((f) => f.split("/").pop()!),
    ];
    chatContext.claude.getState().clear();
    // Auto: o modelo de cada pedido é escolhido pelo tamanho e pela dificuldade.
    const usedModel: ClaudeModelId = model === "auto" ? autoClaudeModel(text, effort) : model;
    const label = model === "auto" ? `Auto · ${claudeModelName(usedModel)}` : claudeModelName(usedModel);
    const history: ChatMessage[] = [
      ...messages.filter((m) => !m.error),
      { role: "user", content: text, context: labels },
    ];
    setMessages([...history, { role: "assistant", content: "", model: label, startedAt: nowMs() }]);
    setInput("");
    setStreaming(true);
    const patchLast = (fn: (m: ChatMessage) => ChatMessage) =>
      setMessages((all) => [...all.slice(0, -1), fn(all[all.length - 1]!)]);
    const body = {
      projectId,
      // Os arquivos anexados vão no último pedido (o servidor junta o resto do contexto).
      messages: history.slice(-20).map((m, i, all) => ({
        role: m.role,
        content: i === all.length - 1 ? attached + m.content : m.content,
      })),
      ...(active && activePath ? { activeFileId: active } : {}),
      ...(selection ? { selection } : {}),
      model: usedModel,
      effort,
    };
    try {
      if (own && desktop) {
        // Conta da pessoa: o servidor monta só o contexto; a chamada sai deste computador.
        const ctx = await api<{
          instructions: string;
          context: string;
          messages: Array<{ role: "user" | "assistant"; content: string }>;
          files: string[];
        }>("/context", { method: "POST", base: AI_BASE, body });
        patchLast((m) => ({ ...m, files: ctx.files }));
        const requestId = crypto.randomUUID();
        ownRequest.current = requestId;
        const off = desktop.claude.onEvent((ev) => {
          if (ev.requestId !== requestId) return;
          if (ev.type === "text")
            patchLast((m) => ({
              ...m,
              content: m.content + ev.text,
              thinkingMs: m.thinkingMs ?? (m.thinking && m.startedAt ? Date.now() - m.startedAt : undefined),
            }));
          else if (ev.type === "thinking")
            patchLast((m) => ({ ...m, thinking: (m.thinking ?? "") + ev.text }));
          else if (ev.type === "error") {
            patchLast((m) => ({ ...m, error: ev.message }));
            if (ev.auth) void qc.invalidateQueries({ queryKey: ["claude", "account"] });
          } else if (ev.type === "done") {
            // Tokens desta resposta no uso do projeto e o que resta no limite por minuto da conta.
            const u = ev.usage;
            useAiUsage.getState().record(
              "claude",
              projectId.toLowerCase(),
              u
                ? {
                    requests: 1,
                    input: u.inputTokens,
                    output: u.outputTokens,
                    cacheRead: u.cacheReadTokens,
                    cacheWrite: u.cacheWriteTokens,
                    cost: claudeCost(ev.model, u) ?? 0,
                  }
                : { requests: 1 },
              { name: detail.project.name, model: ev.model, feature: "Chat" },
            );
            if (ev.limits) useAiUsage.getState().setClaudeLimits(ev.limits);
          }
        });
        try {
          await desktop.claude.chat({
            requestId,
            model: usedModel,
            effort,
            instructions: ctx.instructions,
            context: ctx.context,
            messages: ctx.messages,
          });
        } finally {
          off();
          ownRequest.current = null;
        }
      } else {
        const controller = new AbortController();
        abort.current = controller;
        await streamChat(
          body,
          {
            onContext: (c) => patchLast((m) => ({ ...m, files: c.files })),
            onThinking: (t) => patchLast((m) => ({ ...m, thinking: (m.thinking ?? "") + t })),
            onDelta: (t) =>
              patchLast((m) => ({
                ...m,
                content: m.content + t,
                thinkingMs:
                  m.thinkingMs ?? (m.thinking && m.startedAt ? Date.now() - m.startedAt : undefined),
              })),
            onDone: (info) => {
              useAiUsage
                .getState()
                .record(
                  "kompil",
                  projectId.toLowerCase(),
                  { requests: 1, input: info.usage?.inputTokens, output: info.usage?.outputTokens },
                  { name: detail.project.name, model: info.model, feature: "Chat" },
                );
              void qc.invalidateQueries({ queryKey: ["ai", "status"] });
            },
            onError: (err) =>
              patchLast((m) => ({
                ...m,
                error: err.code === "refusal" ? "O modelo recusou este pedido." : err.message,
              })),
          },
          controller.signal,
        );
      }
    } catch (err) {
      if (!abort.current?.signal.aborted) pushError(errorMessage(err));
    } finally {
      patchLast((m) => ({ ...m, doneAt: Date.now() }));
      setStreaming(false);
      abort.current = null;
      saveClaudeHistory(projectId);
    }
  };

  /** Nova sessão: a conversa atual fica no histórico. */
  const newSession = () => {
    stop();
    useAiChat.setState({ projectId, messages: [], historyId: null });
    inputRef.current?.focus();
  };
  const openSession = (session: ChatSession, index?: number) => {
    stop();
    useAiChat.setState({ projectId, historyId: session.id, messages: session.messages as ChatMessage[] });
    if (index !== undefined) revealMessage(scroller.current, index);
  };

  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "l") {
      e.preventDefault();
      newSession();
    }
  };

  return (
    <div className="flex h-full flex-col" data-chat="claude">
      <ChatSessionBar
        provider="claude"
        projectId={projectId}
        title={messages.length ? sessionTitle(messages.find((m) => m.role === "user")?.content ?? "") : null}
        sessionId={historyId}
        canBookmark={messages.length > 0}
        onNew={newSession}
        onOpen={openSession}
        extra={
          <>
            <MenuLabel>
              <span className="flex items-center gap-1.5 normal-case">
                <span className={cn("h-1.5 w-1.5 rounded-full", own ? "bg-success" : "bg-fg-subtle")} />
                {own
                  ? `Sua conta Anthropic${account.data?.hint ? ` · ${account.data.hint}` : ""}`
                  : "IA do Kompil"}
              </span>
            </MenuLabel>
            {desktop ? (
              own ? (
                <MenuItem
                  icon={<LogOut className="h-3.5 w-3.5" />}
                  onSelect={() =>
                    void desktop!.claude
                      .disconnect()
                      .then(() => qc.invalidateQueries({ queryKey: ["claude", "account"] }))
                  }
                >
                  Desconectar a conta
                </MenuItem>
              ) : (
                <MenuItem icon={<KeyRound className="h-3.5 w-3.5" />} onSelect={() => setConnecting(true)}>
                  Usar minha conta da Anthropic
                </MenuItem>
              )
            ) : null}
            <MenuSeparator />
            <MenuItem
              icon={<Bot className="h-3.5 w-3.5" />}
              shortcut="Ctrl+Shift+A"
              onSelect={() => useAgent.getState().setOpen(true)}
            >
              Agente Claude (vários arquivos)
            </MenuItem>
            <MenuItem
              icon={<WandSparkles className="h-3.5 w-3.5" />}
              onSelect={() => setAutocomplete(!autocomplete)}
            >
              Autocompletar com IA: {autocomplete ? "ligado" : "desligado"}
            </MenuItem>
            <MenuSeparator />
          </>
        }
      />
      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
        {visit?.previousVisitAt && visit.changedFiles > 0 ? (
          <SinceLastVisit since={visit.previousVisitAt} changedFiles={visit.changedFiles} />
        ) : null}
        {messages.length === 0 ? (
          <ChatWelcome
            provider="claude"
            title="Pergunte ao Claude"
            link={{
              label: "Criar o CLAUDE.md",
              hint: "com o que a IA precisa saber sobre o projeto.",
              onClick: () => void send(CLAUDE_MD_PROMPT),
            }}
          />
        ) : (
          <ul className="space-y-5 px-3 py-3">
            {messages.map((m, i) => (
              <li key={i} data-message={i}>
                {m.role === "user" ? (
                  <RequestBubble text={m.content} context={m.context} />
                ) : (
                  <div className="min-w-0">
                    <ResponseHeader provider="claude" />
                    {m.files?.length ? (
                      <details className="mb-1.5 text-[12px] text-fg-subtle">
                        <summary className="cursor-pointer select-none hover:text-fg-muted">
                          Usou {m.files.length} {m.files.length === 1 ? "referência" : "referências"}
                        </summary>
                        <ul className="mt-1 space-y-0.5 pl-3">
                          {m.files.map((f) => (
                            <li key={f}>
                              <button
                                type="button"
                                onClick={() => openPath(f)}
                                className="flex items-center gap-1.5 text-fg-muted hover:text-fg hover:underline"
                              >
                                <FileTypeIcon name={f.split("/").pop()!} className="h-3 w-3" /> {f}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                    {m.thinking ? (
                      <div className="mb-2">
                        <Thought
                          text={m.thinking}
                          live={streaming && i === messages.length - 1 && !m.content}
                          ms={m.thinkingMs}
                        />
                      </div>
                    ) : null}
                    {m.content ? (
                      <Markdown text={m.content} />
                    ) : !m.error && !m.thinking ? (
                      <span className="flex items-center gap-1.5 text-[12px] text-fg-subtle">
                        <Spinner className="h-3 w-3" />{" "}
                        {m.files ? "Escrevendo a resposta…" : "Lendo o projeto…"}
                      </span>
                    ) : null}
                    {m.error ? (
                      <p className="mt-1 rounded-md border border-[color-mix(in_oklab,var(--danger)_45%,transparent)] px-2 py-1 text-[12px] text-[var(--danger)]">
                        {m.error}
                      </p>
                    ) : null}
                    {!m.error && m.doneAt && !(streaming && i === messages.length - 1) ? (
                      <ResponseActions
                        provider="claude"
                        sessionId={historyId}
                        index={i}
                        text={m.content}
                        model={m.model}
                        seconds={
                          m.startedAt ? Math.max(1, Math.round((m.doneAt - m.startedAt) / 1000)) : null
                        }
                        rating={m.rating}
                        onRate={(rating) => {
                          setMessages((all) => all.map((x, j) => (j === i ? { ...x, rating } : x)));
                          saveClaudeHistory(projectId);
                        }}
                        onRetry={() => {
                          const asked = [...messages.slice(0, i)].reverse().find((x) => x.role === "user");
                          if (asked) void send(asked.content);
                        }}
                      />
                    ) : null}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {messages.length === 0 ? (
        <SuggestedActions
          items={SUGGESTIONS}
          onPick={(q) => {
            setInput(q);
            inputRef.current?.focus();
          }}
        />
      ) : null}
      <div onKeyDown={onKey}>
        <ChatInput
          provider="claude"
          value={input}
          onChange={setInput}
          onSubmit={() => {
            const text = input;
            setInput("");
            if (agentMode) sendToSideAgent(wsValue, text.trim());
            else void send(text);
          }}
          onStop={stop}
          busy={streaming}
          inputRef={inputRef}
          placeholder={agentMode ? "Descreva o que o agente deve fazer" : "Pergunte ao Claude"}
          pickers={
            <>
              {canAgent ? (
                <ChatPicker
                  label="Modo"
                  title="Modo: perguntar (responde aqui) ou agente (edita e roda comandos)"
                  value={mode}
                  icon={<CodeXml className="h-3.5 w-3.5 shrink-0" />}
                  options={[
                    {
                      value: "ask",
                      label: "Perguntar",
                      description: "Responde aqui, lendo o projeto; não altera arquivos",
                    },
                    {
                      value: "agent",
                      label: "Agente",
                      description: "Edita arquivos e roda comandos para concluir a tarefa, pedindo permissão",
                    },
                  ]}
                  onChange={(v) => (v === "ask" || v === "agent") && setPrefs({ mode: v })}
                />
              ) : null}
              <ModelPicker
                provider="claude"
                source="Claude"
                value={model}
                autoValue="auto"
                autoDetail="Haiku, Sonnet ou Opus conforme o pedido"
                models={CLAUDE_MODELS.map((m) => ({
                  value: m.id,
                  label: m.name.replace("Claude ", ""),
                  description: m.hint,
                }))}
                intelligence={{
                  value: effort,
                  options: AI_EFFORTS.map((x) => ({ value: x, label: INTELLIGENCE_LABEL[x] ?? x })),
                  onChange: (v) => isAiEffort(v) && setPrefs({ effort: v }),
                }}
                onChange={(v) => (v === "auto" || isClaudeModel(v)) && setPrefs({ model: v })}
              />
            </>
          }
          footer={
            <StatusChip
              icon={<ProviderMark provider="claude" className="h-3.5 w-3.5 shrink-0" />}
              label={own ? "Anthropic Console" : "IA do Kompil"}
              title={
                own
                  ? `Sua conta da Anthropic${account.data?.hint ? ` (${account.data.hint})` : ""} · paga pelo uso, todos os modelos`
                  : "IA do servidor do Kompil"
              }
            >
              <MenuLabel>
                {own
                  ? `Chave do Console${account.data?.hint ? ` · ${account.data.hint}` : ""}`
                  : "Usando a IA do Kompil"}
              </MenuLabel>
              {own ? (
                <>
                  <MenuItem onSelect={() => desktop?.openExternal(CONSOLE_USAGE)}>
                    Uso e limites no Console
                  </MenuItem>
                  <MenuItem
                    icon={<LogOut className="h-3.5 w-3.5" />}
                    onSelect={() =>
                      void desktop!.claude
                        .disconnect()
                        .then(() => qc.invalidateQueries({ queryKey: ["claude", "account"] }))
                    }
                  >
                    Desconectar a conta
                  </MenuItem>
                </>
              ) : desktop ? (
                <MenuItem icon={<KeyRound className="h-3.5 w-3.5" />} onSelect={() => setConnecting(true)}>
                  Usar minha conta da Anthropic (Console)
                </MenuItem>
              ) : null}
              {desktop ? (
                <MenuItem onSelect={() => void runClaudeInTerminal()}>
                  Usar a assinatura do Claude (Pro/Max) no terminal
                </MenuItem>
              ) : null}
            </StatusChip>
          }
        />
      </div>
    </div>
  );
}
